import { Injectable } from '@angular/core';
import { CommodityViewModel } from '@models/commodities.class';
import { MarketViewModel } from '@models/market.class';
import moment from 'moment-timezone';
import { SOLACE_SUBSCRIPTIONS } from 'src/app/app.constants';
import { ChangeCharacterFromIssue } from 'src/shared/utility/change-character';
import {
  getMarketName,
  MarketType,
} from 'src/solace/constants/marketType.enum';
import { MarketHecho } from 'src/solace/models/market/marketHecho.class';
import { MarketHechoBody } from 'src/solace/models/market/marketHechoBody.class';
import { MarketItem } from 'src/solace/models/market/marketItem.class';
import { MarketPostura } from 'src/solace/models/market/marketPostura.class';
import { MarketPosturaBody } from 'src/solace/models/market/marketPosturaBody.class';
import { SharedSolaceMiddlewareService } from 'src/solace/sharedSolace.middleware.service';
import { SharedSolaceStreamService } from 'src/solace/sharedSolace.stream.service';
import { CommodityGroups } from './models/solace/solaceCommoditiesType.class';

@Injectable()
export class SharedSolaceMainService {
  private solaceTopics: any[];
  private hash = '';

  constructor(
    private sharedSolaceMiddlewareService: SharedSolaceMiddlewareService,
    private sharedSolaceStreamService: SharedSolaceStreamService,
  ) {}

  get isConnected(): boolean {
    return this.sharedSolaceMiddlewareService.isSolaceConnected;
  }

  /**
   * @method init : Initialize solace ith solace topics and inject data into nacData, sicData and indexesData
   * @param solaceConfigurations : Contains solace connection configuration
   * @param solaceTopics : Contains an array of topics
   * @param nacData : Contains an array of BMV symbols
   * @param sicData : Contains an array of SIC symbols
   * @param indexes : Contains an array of Indexes symbols
   * @param commodities : Contains an array of Commodities symbols
   * @param currencies : Contains an array of Currencies symbols
   */

  init(
    solaceConfigurations: any,
    solaceTopics: any[],
    nacData: any[],
    sicData: any[],
    indexes: CommodityViewModel[],
    commodities?: CommodityViewModel[],
    currencies?: CommodityViewModel[],
  ): boolean {
    this.initSolaceSubscriptions(solaceTopics);
    if (
      this.sharedSolaceMiddlewareService.init(
        solaceConfigurations,
        this.initialSubscriptions(solaceTopics),
      )
    ) {
      this.setMarketData(MarketType.BMV, nacData);
      this.setMarketData(MarketType.SIC, sicData);
      this.setCommoditiesByType(CommodityGroups.indexes, indexes);
      this.setCommoditiesByType(CommodityGroups.commodities, commodities);
      this.setCommoditiesByType(CommodityGroups.currencies, currencies);

      return true;
    }

    return false;
  }

  initUsa(
    solaceConfigurations: any,
    solaceTopics: any[],
    marketData: MarketViewModel[],
  ): boolean {
    this.initSolaceSubscriptions(solaceTopics);
    if (
      this.sharedSolaceMiddlewareService.init(
        solaceConfigurations,
        this.initialSubscriptions(solaceTopics),
      )
    ) {
      this.setMarketData(MarketType.USA, marketData);

      return true;
    }

    return false;
  }

  /**
   * @method initSolaceSubscriptions : Set the value subscribed when initializing
   * @param solaceTopics : Array of topics
   */
  initSolaceSubscriptions(solaceTopics: any[]) {
    this.solaceTopics = solaceTopics.map((value) => {
      const subscribed =
        value.subscriptionTopic === SOLACE_SUBSCRIPTIONS.INDICES;

      return { ...value, subscribed };
    });
  }

  /**
   * @method initSolaceSubscriptions : Parse an array of topics to return just the one we need.
   * @param solaceTopics : Array of topics
   */
  initialSubscriptions(solaceTopics: any[]): any[] {
    return solaceTopics.filter((topic: any) => {
      return topic.subscriptionTopic === SOLACE_SUBSCRIPTIONS.INDICES;
    });
  }

  /**
   * @method connect : Connects to the stream of solace
   */
  connect(): void {
    this.sharedSolaceMiddlewareService.connectSolace();
  }

  /**
   * @method disconnect : Disonnects to the stream of solace
   */
  disconnect(): void {
    this.sharedSolaceMiddlewareService.disconnectSolace();
  }

  /**
   * @method updateData : Update market data From the data in nac, sic and indexes.
   * @param nacData : Contains an array of BMV symbols
   * @param sicData : Contains an array of SIC symbols
   * @param indexesData : Contains an array of Indexes symbols
   */
  updateData(
    nacData: any[],
    sicData: any[],
    indexesData: CommodityViewModel[],
    commoditiesData?: CommodityViewModel[],
    currenciesData?: CommodityViewModel[],
  ): void {
    this.setMarketData(MarketType.BMV, nacData);
    this.setMarketData(MarketType.SIC, sicData);
    this.setCommoditiesByType(CommodityGroups.indexes, indexesData);
    this.setCommoditiesByType(CommodityGroups.commodities, commoditiesData);
    this.setCommoditiesByType(CommodityGroups.currencies, currenciesData);
  }

  updateIndexes(indexesData: CommodityViewModel[]): void {
    this.setCommoditiesByType(CommodityGroups.indexes, indexesData);
  }

  updateMarketData(
    marketType: MarketType,
    marketData: MarketViewModel[],
  ): void {
    this.setMarketData(marketType, marketData);
  }

  /**
   * @method changeSubscriptionFlag : Updates the subcribed flag to a topic
   * @param topic : Contains a topic's object
   * @param flag : Contains a boolean value
   */
  changeSubscriptionFlag(topic: string, flag: boolean = true) {
    this.solaceTopics = this.solaceTopics.map((solacePropertieTopic: any) => {
      if (solacePropertieTopic.subscriptionTopic.indexOf(topic) !== -1) {
        solacePropertieTopic.subscribed = flag;
      }
      return solacePropertieTopic;
    });
  }

  /**
   * @method getTopic : Returns a topic's object with the subscription name
   * @param topic : Contains a topic's object to be used
   * @param issue : Contains a string that will be attached to the subscription topic if included
   */
  getTopic(topic: string, issue: string) {
    if (this.solaceTopics === undefined) {
      return;
    }

    const solaceTopic = this.solaceTopics.filter((property: any) => {
      if (property.PreviousSubscriptionTopic !== undefined) {
        property.subscriptionTopic = property.PreviousSubscriptionTopic;
      } else {
        property.PreviousSubscriptionTopic = property.subscriptionTopic;
      }

      return property.subscriptionTopic.indexOf(topic) !== -1;
    });

    if (issue.length < 2) {
      solaceTopic[0].correlationKey = {
        Symbol: '*',
      };
    } else {
      solaceTopic[0].correlationKey = {
        Symbol: issue,
      };
      solaceTopic[0].subscriptionTopic =
        solaceTopic[0].subscriptionTopic.substring(
          0,
          solaceTopic[0].subscriptionTopic.length - 1,
        ) + issue;
    }
    return solaceTopic[0];
  }

  /**
   * @method subscribeTopic : Subscribe to the topic given
   * @param topic : Contains a topic's object to be used
   * @param issue : Contains a string that will be attached to the subscription topic if included
   */
  subscribeTopic(topic: string, issue: string = '', byPass: boolean = false) {
    const toSubscribe = this.getTopic(topic, issue);
    // if (!toSubscribe.subscribed || byPass) {
    this.changeSubscriptionFlag(topic, true);
    this.sharedSolaceMiddlewareService.subscribeTopic(toSubscribe);
    // }
  }

  /**
   * @method unSubscribeTopic : Unsubscribe to a topic given
   * @param topic : Contains a topic's object to be used
   * @param issue : Contains a string that will be attached to the subscription topic if included
   */
  unSubscribeTopic(topic: string, issue: string = '', byPass: boolean = false) {
    const toUnSubscribe = this.getTopic(topic, issue);
    // if (toUnSubscribe.subscribed || byPass) {
    this.changeSubscriptionFlag(topic, false);
    this.sharedSolaceMiddlewareService.unSubscribeTopic(toUnSubscribe);
    // }
  }

  /**
   * @method updateHash : Update hash to every single topic subscription.
   * @param newHash : Contains the new hash recovered from the downgraded or upgraded session.
   */
  updateHash(newHash: any): void {
    const WAIT_TIME = 60;

    this.hash = newHash;

    if (this.solaceTopics !== undefined) {
      this.solaceTopics.map((topic: any) => {
        topic.serviceToken = newHash;
      });
    } else {
      setTimeout(() => {
        this.updateHash(this.hash);
      }, WAIT_TIME);
    }
  }

  /**
   * @method setMarketData : Define market information to be used in the application from the formatted data
   * @param marketType : Contains an id to tell which market is currently being used.
   * @param data : Contains the data of all the issues (solace stream's data)
   */
  public setMarketData(marketType: MarketType, data: MarketViewModel[]): void {
    for (const item of data) {
      item.securityId = new ChangeCharacterFromIssue().ChangeAmpersand(
        item.securityId,
      );
      const hechoBody: MarketHechoBody = new MarketHechoBody();
      hechoBody.securityId = item.securityId;
      hechoBody.variationPercentage = item.variationPercentage || 0;
      hechoBody.lastPrice =
        (item.lastPrice > 0 ? item.lastPrice : item.closePrice) || 0;
      hechoBody.volume = item.volume || 0;
      hechoBody.operationVolume = item.volume || 0;
      hechoBody.maxPrice = item.maxPrice || 0;
      hechoBody.minPrice = item.minPrice || 0;
      hechoBody.weightedAveragePrice = item.weightedAveragePrice;
      hechoBody.bmvWeightedAvgPrice = 0;
      hechoBody.openPrice = item.openPrice || 0;
      hechoBody.volumeInSixMonths = item.volumeInSixMonths;
      hechoBody.lastWithLot =
        (item.lastPrice > 0 ? item.lastPrice : item.closePrice) || 0;
      hechoBody.Time = moment().format('HH:mm:ss');
      hechoBody.ipcParticipationRate = item.ipcParticipationRate || 0;

      const hecho: MarketHecho = new MarketHecho();
      hecho.symbol = item.securityId;
      hecho.body = hechoBody;
      hecho.closePrice = item.closePrice;

      const posturaBody: MarketPosturaBody = new MarketPosturaBody();
      posturaBody.BuyPrice = item.bid || 0;
      posturaBody.BuyVolume = item.bidSize || 0;
      posturaBody.BuyOrdersNum = 0;
      posturaBody.SellPrice = item.ask || 0;
      posturaBody.SellVolume = item.askSize || 0;
      posturaBody.SellOrdersNum = 0;

      const postura: MarketPostura = new MarketPostura();
      postura.body = [posturaBody];

      const marketItem: MarketItem = new MarketItem();
      marketItem.symbol = item.securityId;
      marketItem.hechos = hecho;
      marketItem.posturas = postura;
      marketItem.instrumentType = marketType;
      marketItem.isInWatchList = false;
      marketItem.isTrade = false;
      marketItem.instrumentId = item.instrumentId;
      marketItem.externalId = item.externalId;

      const marketName = getMarketName(marketType);

      if (
        this.sharedSolaceStreamService.marketMaps[marketName].has(
          item.securityId,
        )
      ) {
        const currentSymbol: any =
          this.sharedSolaceStreamService.getMarketElement(
            marketName,
            item.securityId,
          );

        currentSymbol.hechos.closePrice = hecho.closePrice;

        Object.assign(currentSymbol.hechos.body, hecho.body);
      } else {
        this.sharedSolaceStreamService.marketMaps[marketName].set(
          item.securityId,
          this.sharedSolaceStreamService.markets[marketName].push(marketItem),
        );
      }
    }
  }

  /**
   * @method setCommoditiesByType : Define commodities information by type to be used in the application from the formatted data
   * @param commodities : Contains the data of all the issues (solace stream's data)
   */
  private setCommoditiesByType(
    type: CommodityGroups,
    commodities: CommodityViewModel[],
  ): void {
    this.sharedSolaceStreamService.setCommoditiesByType(type, commodities);
  }
}
